<template lang="pug">
  .details.column.width-100.relative
    language-selector.language-selector(
      v-if='content.activatedLanguages && availableLanguages.length'
      :languages='availableLanguages'
      @change-resource-locale='updateLocale'
      view='content'
      :viewId='content.id'
      inner
    )
    #rationale
      h3.title-medium.m-bottom-1 {{ innerI18n.t('detailProgram.foundation') }}
      krea-text-editor.m-bottom-2(
        name='rationale'
        :value='activeLocalizedContent.rationale || ""'
        placeholderHeight='200px'
        height='350px'
        :info='rationaleInfo'
        :placeholder='innerI18n.t("krea.educationalContent.details.rationalePlaceholder", {content: contentTypeTranslation})'
        undo redo bold italic underline orderedList unorderedList small :maxCharacters='400'
        edit
        @input='updateProgram'
      )
    #pedagogicalObjective
      h3.title-medium.m-bottom-1 {{ innerI18n.t('detailProgram.pedagogicalObjective') }}
      krea-text-editor.m-bottom-2(
        name='pedagogicalObjective'
        :value='activeLocalizedContent.pedagogicalObjective || ""'
        placeholderHeight='150px'
        height='350px'
        :info='pedagogicalObjectiveInfo'
        :placeholder='innerI18n.t("krea.educationalContent.details.pedagogicalGoalPlaceholder")'
        undo redo bold italic underline orderedList unorderedList small :maxCharacters='1000'
        edit
        @input='updateProgram'
      )
    v-popover(
      placement='bottom-start'
      :delay='{show: 100, hide: 600}'
      trigger='hover click'
      popoverClass='krea-actions-popover'
      :container='false'
    )
      button.m-top-4.summary-button.small.with-icon.primary-button(@click='triggerSummaryLink' v-if='activeLocalizedContent.summaryLink')
        simple-svg.m-right-1.white-icon(:filepath='downloadIcon' width='20px' height='20px')
        | {{ innerI18n.t("krea.educationalContent.details.summaryLink", {content: contentTypeTranslation}) }}
      button.m-top-4.summary-button.small.with-icon.terciary-button(@click='triggerSummaryLink' v-else)
        simple-svg.m-right-1(:filepath='uploadIcon' width='20px' height='20px')
        | {{ innerI18n.t("krea.educationalContent.details.uploadSummary", {content: contentTypeTranslation}) }}
      template(slot='popover')
        actions(
          :linkValue='activeLocalizedContent.summaryLink'
          :updateFile='editSummaryLink'
          :updateLink='editSummaryLink'
          @removeFile='removeSummaryLink'
          placement='start'
          fileLink
          :fileValue='activeLocalizedContent.summaryLink'
          :file='!!activeLocalizedContent.summaryLink' fileType='pdf'
          :info='summaryLinkInfo'
        )
</template>

<script>
import KreaTextEditor from '@/components/Krea/Inputs/KreaTextEditor'
import LanguageSelector from '@/components/Krea/LanguageSelector'
import { mapActions, mapState } from 'vuex'
import { languagesCodesToString } from '../../../constants/languages'
import uploadIcon from '@/assets/ic_upload.svg'
import downloadIcon from '@/assets/ic_download.svg'
import { contentType } from '../../../utils/constants'
import { innerI18n } from '../../../main'
import Actions from '@/components/Krea/Inputs/Actions/Actions'
import { kreaCustomDetailsTypes } from '@/constants/kreaCustom'

export default {
  components: { KreaTextEditor, LanguageSelector, Actions },
  data() {
    return {
      downloadIcon,
      uploadIcon,
      innerI18n
    }
  },
  computed: {
    ...mapState({
      content: state => state.kreaEducationalContent.content,
      activeLanguage: state => state.kreaEducationalContent.activeLanguage,
      innerLocale: state => state.behavior.innerLocale,
      customFields: state => state.userInfo.customFields
    }),
    kreaContentGuide() {
      return process.env[`VUE_APP_KREA_CREATE_CONTENT_GUIDE_${this.innerLocale}`]
    },
    kreaProgramSummaryGuidePdf() {
      const customFile = this.customFields.customizedDetails &&
        this.customFields.customizedDetails.find(det => det.type === kreaCustomDetailsTypes.pdf)
      return customFile
        ? customFile.file
        : process.env[`VUE_APP_KREA_PROGRAM_SUMMARY_GUIDE_PDF_${this.innerLocale}`]
    },
    kreaProgramSummaryGuideDocx() {
      const customFile = this.customFields.customizedDetails &&
        this.customFields.customizedDetails.find(det => det.type === kreaCustomDetailsTypes.editable)
      return customFile
        ? customFile.file
        : process.env[`VUE_APP_KREA_PROGRAM_SUMMARY_GUIDE_DOCX_${this.innerLocale}`]
    },
    kreaProjectSummaryGuidePdf() {
      const customFile = this.customFields.customizedDetails &&
        this.customFields.customizedDetails.find(det => det.type === kreaCustomDetailsTypes.pdf)
      return customFile
        ? customFile.file
        : process.env[`VUE_APP_KREA_PROJECT_SUMMARY_GUIDE_PDF_${this.innerLocale}`]
    },
    kreaProjectSummaryGuideDocx() {
      const customFile = this.customFields.customizedDetails &&
        this.customFields.customizedDetails.find(det => det.type === kreaCustomDetailsTypes.editable)
      return customFile
        ? customFile.file
        : process.env[`VUE_APP_KREA_PROJECT_SUMMARY_GUIDE_DOCX_${this.innerLocale}`]
    },
    activeLocalizedContent() {
      return this.content.localizedEducationalPrograms.find(pr => languagesCodesToString[pr.locale] === this.activeLanguage)
    },
    contentTypeTranslation() {
      const contentTypeName = Object.keys(contentType).find(key => this.content.contentType === contentType[key])
      return innerI18n.t(`krea.educationalContent.${contentTypeName}`).toLowerCase()
    },
    availableLanguages() {
      return Object.keys(this.content.activatedLanguages || {})
    },
    rationaleInfo() {
      return {
        title: innerI18n.t('krea.educationalContent.details.rationaleInfoTitle'),
        guideLink: { msg: innerI18n.t('krea.educationalContent.details.contentGuideName'), link: this.kreaContentGuide },
        tips: [innerI18n.t('krea.educationalContent.details.rationaleInfo', { content: this.contentTypeTranslation })]
      }
    },
    pedagogicalObjectiveInfo() {
      return {
        title: innerI18n.t('krea.educationalContent.details.pedagogicalGoalInfoTitle'),
        guideLink: { msg: innerI18n.t('krea.educationalContent.details.contentGuideName'), link: this.kreaContentGuide },
        tips: [innerI18n.t('krea.educationalContent.details.pedagogicalGoalInfo', { content: this.contentTypeTranslation })]
      }
    },
    summaryLinkInfo() {
      return {
        title: innerI18n.t('krea.educationalContent.details.summaryLinkInfoTitle', { content: this.contentTypeTranslation }),
        guideLink: [
          {
            msg: innerI18n.t('krea.educationalContent.details.summaryGuidePdfName'),
            link: this.content.contentType === contentType.program ? this.kreaProgramSummaryGuidePdf : this.kreaProjectSummaryGuidePdf
          },
          {
            msg: innerI18n.t('krea.educationalContent.details.summaryGuideDocxName'),
            link: this.content.contentType === contentType.program ? this.kreaProgramSummaryGuideDocx : this.kreaProjectSummaryGuideDocx
          }
        ],
        tips: [innerI18n.t('krea.educationalContent.details.summaryLinkInfo', { content: this.contentTypeTranslation })]
      }
    }
  },
  methods: {
    ...mapActions([
      'editKreaLocalizedContentField',
      'setKreaActiveContentLanguage',
      'showElement',
      'changeInnerLocale'
    ]),
    updateProgram(value, field) {
      this.editKreaLocalizedContentField({ field, value, locale: this.activeLanguage })
    },
    updateLocale(locale) {
      this.changeInnerLocale(locale)
      this.setKreaActiveContentLanguage(locale)
    },
    editSummaryLink(value) {
      this.editKreaLocalizedContentField({ field: 'summaryLink', value, locale: this.activeLanguage })
    },
    removeSummaryLink() {
      this.editKreaLocalizedContentField({ field: 'summaryLink', value: null, locale: this.activeLanguage })
    },
    triggerSummaryLink() {
      if (this.activeLocalizedContent.summaryLink) window.open(this.activeLocalizedContent.summaryLink, '_blank').focus()
      else {
        this.showElement({
          element: 'kreaFileUploaderModal',
          props: {
            type: 'pdf',
            link: true,
            linkType: 'pdf',
            updateLink: this.editSummaryLink,
            updateFile: this.editSummaryLink
          }
        })
      }
    }
  }
}
</script>

<style lang="scss" scoped>
@import 'src/scss/variables/colors';

.details {
  background-color: $white;
  border-radius: 8px;
  border: 1px solid $black;
  margin-top: -60px;
  padding: 48px;
  min-height: 550px;
}

.language-selector{
  position: absolute;
  z-index: 10;
  top: 20px;
  right: 20px;
}

.summary-button{
  max-width: none;
}
</style>
